import {
  Component,
  ElementRef,EventEmitter,
  HostBinding,
  HostListener,
  Input,
  NgModule,
  Output,
  ViewChild,
} from '@angular/core';
import { CLF_2_MAT, ButtonType, MAT_ICONS, MAT_ICON_NAMES} from '@wipo/w-angular/shared';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconDirective, IconDirectiveModule } from '@wipo/w-angular/directives';
import { WButtonDownloadComponent } from './w-button-download.component';
import { WButtonBaseDirective } from './w-button.base.directive';
import { WDropdownComponent, WDropdownModule } from '@wipo/w-angular/w-dropdown';
import { takeUntil } from 'rxjs/operators';
import { FFacetModule } from '@wipo/w-angular/f-facet';

const CLF_ICONS = Object.keys(CLF_2_MAT) as string[]; 
const SVG_ICONS = IconDirective.icons;
type SvgIcon = typeof SVG_ICONS[number];

@Component({
    selector: 'button[w-button]',
    templateUrl: './w-button.component.html',
    host: {
        '[class.b-button]': 'buttonType === "header" || !isIcon',
        // sizes
        '[class.b-button-medium]': '(buttonType !== "header" && size==="medium") || (buttonType!=="text" && !isTertiary && !size )', // medium buttons treated differently including some JS 
        '[class.b-button-large]': 'buttonType!=="header" && size==="large"',
        // for some reason we have 2 "-small" css classes
        // w-sticky bar needs -- dash version
        '[class.b-button--small]': 'buttonType==="header" || size==="small" && (!isIcon || !isTertiary)',
        // w-action bar needs both classes
        '[class.b-button-small]': 'size==="small"',
        '[class.b-button-tertiary]': 'isTertiary',
        // dark
        '[class.b-button--is-dark]': 'buttonType==="header"',
        // primary/secondary
        '[class.b-button--is-type_primary]': 'isDefault && size!=="medium" && buttonType==="text"',
        '[class.primary]': 'isDefault && size==="medium"', // in JSF used only in conjuction with medium size
        '[class.b-button--is-type_secondary]': 'isSecondary && buttonType==="text"',
        // disabled
        '[class.b-button--is-disabled]': 'disabled && buttonType==="text"',
        '[class.b-button-medium--is-disabled]': 'disabled && buttonType!="text" && size==="medium"',
        '[class.b-button-small--is-disabled]': 'disabled && buttonType!="text" && size==="small"',
        '[class.b-button-large--is-disabled]': 'disabled && buttonType!="text" && size==="large"',
        // override background color (& bold white text)
        '[class.b-smooth]': 'bgColor',
        '[style.background-color]': 'bgColor ? bgColor : ""',
        '[style.font-weight]': 'bgColor ? "500" : ""',
        '[style.color]': 'bgColor ? "white" : ""',
        '[style.border]': 'bgColor ? "none" : ""',
        // w-filter activated button
        '[class.filtered]': 'isActive',
        // always type=button (to avoid submit triggered in form)
        '[attr.type]': '"button"',
        '[class.b-button--image]': 'buttonType==="tmview"',
        '[class.b-single-icon]': "!hasLabel && (iconStart || iconEnd)",
        '[class.b-button--with-flag]': '!!flagSrc'
    },
    standalone: false
})
/*
@id w-button
@name w-button
@status release
@description directive that modifies BUTTON html element. Supports text and icon buttons
*/
export class WButtonComponent extends WButtonBaseDirective {
  @Input() isDefault: boolean= false; // primary
  @Input() isSecondary: boolean = false;
  @Input() isActive: boolean= false; // used to mark active selection in w-filter component

  // apart from type=text or tertiary, all other types indicate appropriate (CLF) icons or ULF (material) icons
  @Input() buttonType: ButtonType = 'text'; // @options [text, search, edit, up, down, delete, copy, addressbook, calendar,download,dropdown,tmview,tertiary,header, any icon from standard material icon set]
  
  /* label as span */
  @ViewChild('labelTemplate') labelSpan?: ElementRef<HTMLElement>;
  @ViewChild('dd') dropdown?: WDropdownComponent;
  hasLabel: boolean = false;

  
  get iconType(): keyof typeof CLF_2_MAT | undefined {
    return CLF_ICONS.includes(this.buttonType) || MAT_ICON_NAMES.includes(this.buttonType) ? (this.buttonType as keyof typeof CLF_2_MAT): undefined;
  }
  // keyof typeof icons
  get svgIconType(): SvgIcon | undefined {
    const typ = this.buttonType as any;
    return SVG_ICONS.includes(typ) ? (typ as SvgIcon): undefined;
  }
  // medium has special treatment, since ULF show large and medium the same
  @Input() size = ''; // @options [small, medium, large, <empty string>]

  @Input() bgColor: string = ''; // override the background color
  @Input('icon-start') iconStart: string = ''; // material icon at the start of label, used in text and tertiary button
  @Input('icon-end') iconEnd: string = ''; // material icon at the end of label, used in text and tertiary button
  @Input() flag: string = ''; // ISO/member code for a leading flag image

  get flagSrc(): string {
    const code = (this.flag || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? `assets/i/flags/${code}.svg` : '';
  }

  @HostBinding('attr.icon-button') get iconButton() { return this.isIcon && this.buttonType !== 'tmview' ? '' : null; }

  @Output() wClick = new EventEmitter<HTMLButtonElement>(); // emits on click
 
  element: HTMLElement;
  constructor(protected elementRef: ElementRef) { super(); this.element = elementRef.nativeElement;}

  get iconClass() {
    switch (this.buttonType) {
      case 'delete': return 'b-icon--trash';
      case 'copy': return 'b-icon--file';
      default: return `b-icon--${this.buttonType}`;
    }
  }
  get isTertiary(){
    return this.buttonType === 'tertiary';
  }
  get isText(){
    return this.buttonType === 'text';
  }
  get isIcon() {
    return ['edit', 'delete', 'copy', 'up', 'down', 'addressbook', 'search', 'calendar', 'dropdown', 'tmview'].includes(this.buttonType)
      || (MAT_ICON_NAMES.includes(this.buttonType));
  }

  matIcon(name: keyof typeof CLF_2_MAT | undefined): string | undefined {
    // console.log(name, name ? (mat[name] || mat2[name] || 'search' ): undefined);
    return name ? (CLF_2_MAT[name] || MAT_ICONS[name] || 'search' ): undefined;
  }
  @HostListener('click', ['$event.target'])
  clicked(button: HTMLButtonElement){
    this.wClick.emit(button);
  }
  ngAfterViewInit(): void {
    // if we have button with label, watch it
    if (this.labelSpan) this.isEmptyElement(this.labelSpan?.nativeElement)?.pipe(takeUntil(this.destruction)).subscribe(e => this.hasLabel = !e);
  }

}
@NgModule({
  imports: [CommonModule, FormsModule, FFacetModule, IconDirectiveModule, WDropdownModule],
  exports: [WButtonComponent, WButtonDownloadComponent],
  declarations: [WButtonComponent, WButtonDownloadComponent]
})
export class WButtonModule { }
